/**
 * HorizontalCarousel — wrapper for snap-scroll horizontal lists.
 *
 * Why: the existing `flex overflow-x-auto snap-x` pattern works fine on touch
 * devices but on desktop users have no obvious affordance — mouse wheel
 * scrolls vertically and there are no prev/next buttons. This wrapper:
 *   1. Adds <- / -> chevron buttons on hover for ≥md screens, only when there
 *      is actually something to scroll to.
 *   2. Enables click-and-drag with the mouse (pointer events).
 *   3. Keeps native momentum + snap on touch unchanged.
 *
 * Usage:
 *   <HorizontalCarousel testId="featured-carousel">
 *     {items.map(...)}
 *   </HorizontalCarousel>
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function HorizontalCarousel({
  children,
  testId,
  className = '',
  itemClassName = '',
}) {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateButtons = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth - 1;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < max);
  }, []);

  useEffect(() => {
    updateButtons();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateButtons, { passive: true });
    const ro = new ResizeObserver(updateButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateButtons);
      ro.disconnect();
    };
  }, [updateButtons, children]);

  // Click-and-drag (desktop only — touch already handled natively)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;
    let moved = false;

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse') return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      pointerId = e.pointerId;
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      el.scrollLeft = startScroll - dx;
    };
    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      if (moved) {
        // Prevent the click that follows the drag from triggering navigation.
        const stop = (clickEvt) => {
          clickEvt.preventDefault();
          clickEvt.stopPropagation();
        };
        el.addEventListener('click', stop, { capture: true, once: true });
        // Safety: drop the listener if no click arrived.
        setTimeout(() => el.removeEventListener('click', stop, { capture: true }), 200);
      }
      try { el.releasePointerCapture(pointerId); } catch (_) { /* ignore */ }
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('pointerleave', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('pointerleave', onPointerUp);
    };
  }, []);

  const scrollByAmount = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handlePrev = () => scrollByAmount(-Math.max(280, scrollerRef.current?.clientWidth * 0.7 || 280));
  const handleNext = () => scrollByAmount(Math.max(280, scrollerRef.current?.clientWidth * 0.7 || 280));

  return (
    <div className={`relative group ${className}`} data-testid={testId}>
      {/* Prev button — hidden on touch, only visible when scrollable */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={handlePrev}
          aria-label="Anterior"
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 hover:bg-gray-50 active:scale-95 transition-all opacity-0 group-hover:opacity-100"
          data-testid={testId ? `${testId}-prev` : 'carousel-prev'}
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
      )}

      <div
        ref={scrollerRef}
        className={`flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide select-none cursor-grab active:cursor-grabbing ${itemClassName}`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>

      {canScrollRight && (
        <button
          type="button"
          onClick={handleNext}
          aria-label="Próximo"
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 hover:bg-gray-50 active:scale-95 transition-all opacity-0 group-hover:opacity-100"
          data-testid={testId ? `${testId}-next` : 'carousel-next'}
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      )}
    </div>
  );
}
